import { SummaryTracking } from "@/lib/tracking/summary";
import { useQuery } from "@tanstack/react-query";
import { Tabs, Tile, NumberFormatter, formatNumber } from "@uicore";
import { Card, NativeSelect, Stack, Tooltip } from "@altimateai/lego";
import { useState, useEffect } from "react";
import classes from "../summary.module.scss";
import AggregateFilter from "./AggregateFilter";
import BreakdownSkeleton from "./BreakdownSkeleton";
import { getSavingsBreakdown } from "@/helpers/apis";
import AssistedSavingsSection from "./AssistedSavingsSection";
import { AggregateByValues, TotalCostEntities } from "./constants";
import SavingsInFuture from "./SavingsInFuture";
import {
  AssistedSavingsResponse,
  AutoTuneSavingsSummaryResponse,
  OverallSavingsResponse,
  OverallSavingsEstimateResponse,
} from "../context/types";
import AutonomousSavingsSection from "./AutonomousSavingsSection";
import { formatDate } from "./utils";
import AccordionTitle from "./AccordionTitle";
import FailureComponent from "./FailureComponent";
import { useAppState } from "@/modules/app/useAppContext";
import {
  getDateByStartOfDayWithoutTimeoffset,
  getDateByEndOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import dayjs from "dayjs";
import styles from "./styles.module.scss";
import { Typography } from "@altimateai/lego";
import { useSelectedInstance } from "../SummaryNew";

interface Props {
  isFuture?: boolean;
  currentStartDate: Date;
  currentEndDate: Date | null;
}

const SavingsBreakdown = ({ isFuture, currentStartDate, currentEndDate }: Props) => {
  const { currency, rbacEnabled, userGroup, userGroupsLoaded } = useAppState();
  const selectedInstance = useSelectedInstance();
  const [savingsType, setSavingsType] = useState("autonomous");
  const [aggregateBy, setAggregateBy] = useState(
    isFuture ? AggregateByValues.Monthly : AggregateByValues.Daily
  );
  const [savingsPeriod, setSavingsPeriod] = useState("next1Year");

  const yearMode = aggregateBy === AggregateByValues.Monthly;

  // Get the cost entities based on AI services enablement
  const totalCostEntities = TotalCostEntities(rbacEnabled, userGroup);

  const onAggregateSelect = (value: string) => {
    setAggregateBy(value);
  };

  const getEndPoint = () => {
    if (isFuture) {
      return `${savingsType}/estimate`;
    }

    return savingsType;
  };

  const { isLoading, isFetching, data, error } = useQuery({
    queryKey: [
      `savingsBreakdown-${isFuture ? "future" : "past"}`,
      savingsType,
      currentStartDate?.getTime(),
      currentEndDate?.getTime(),
      aggregateBy,
      selectedInstance,
      userGroup?.id,
    ],
    queryFn: () =>
      getSavingsBreakdown(getEndPoint(), {
        start_date: getDateByStartOfDayWithoutTimeoffset(currentStartDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(currentEndDate)?.toISOString(),
        aggregation_level: aggregateBy,
        ...(selectedInstance && selectedInstance !== "all" && { instance_id: selectedInstance }),
      }),
    enabled: Boolean(!isFuture && currentStartDate && currentEndDate && userGroupsLoaded),
  });

  useEffect(() => {
    if (error) {
      SummaryTracking.dataFetchFailed(error);
    }
  }, [error]);

  const {
    data: overallSavingsData,
    error: overallSavingsError,
    isLoading: isSavingsLoading,
  } = useQuery({
    queryKey: [
      `overallSavingsBreakdown-${isFuture ? "future" : "past"}`,
      currentStartDate?.getTime(),
      currentEndDate?.getTime(),
      aggregateBy,
      savingsPeriod,
      selectedInstance,
      userGroup?.id,
    ],
    queryFn: () =>
      getSavingsBreakdown(isFuture ? `overall/estimate` : "overall", {
        start_date: getDateByStartOfDayWithoutTimeoffset(currentStartDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(currentEndDate)?.toISOString(),
        aggregation_level: aggregateBy,
        savings_period: savingsPeriod,
        ...(selectedInstance && selectedInstance !== "all" && { instance_id: selectedInstance }),
      }) as Promise<OverallSavingsResponse | OverallSavingsEstimateResponse> | undefined,
    enabled: Boolean(currentStartDate && currentEndDate && userGroupsLoaded),
  });

  useEffect(() => {
    if (overallSavingsError) SummaryTracking.savingsBreakdownLoadFailed(overallSavingsError);
  }, [overallSavingsError]);

  const getSavingsValueOrRange = () => {
    if (!overallSavingsData) {
      return null;
    }

    const { max_total_autonomous_savings, min_total_autonomous_savings, total_autonomous_savings } =
      overallSavingsData;
    const value = isFuture
      ? (overallSavingsData as OverallSavingsEstimateResponse)?.total_autonomous_savings_estimate
      : total_autonomous_savings;
    const showRange = max_total_autonomous_savings > 0 && min_total_autonomous_savings > 0;

    if (showRange) {
      const rangeTooltip = `Between ${formatNumber(min_total_autonomous_savings, {
        currency,
      })} to ${formatNumber(max_total_autonomous_savings, { currency })}`;
      return (
        <Tooltip title={rangeTooltip}>
          <span>
            <NumberFormatter value={value} options={{ currency }} noTooltip />
          </span>
        </Tooltip>
      );
    }
    return <NumberFormatter value={value} options={{ currency }} />;
  };

  if (isLoading || isSavingsLoading) {
    return <BreakdownSkeleton type="section" />;
  }

  return (
    <Card
      className={classes.section_card}
      id={isFuture ? "tour-summary-savings-future" : "tour-summary-savings"}
    >
      <AccordionTitle
        title={
          <Stack className="al-w-full">
            <Typography variant="section-heading">Savings</Typography>
            <div className="spacer" />
            {isFuture && (
              <NativeSelect
                id="summary-savings-period-dropdown"
                onChange={e => {
                  setSavingsPeriod(e);
                }}
                className={styles.period_selection}
                value={savingsPeriod}
                options={[
                  { value: "thisYear", label: "This Year" },
                  { value: "next1Year", label: "Next 1 Year" },
                ]}
              />
            )}
          </Stack>
        }
        howToText="How to read the charts"
        howToContent={
          <p>
            {isFuture
              ? "These section show the Snowflake savings data for the period specified for Future State. Saving may be coming from autonomous agent actions or your team implementing the recommendations shown as opportunities. We estimate future savings for a month based on the analysis of different parameters, including savings realized in the past (if any). Then, we predict future savings for the selected period by simply multiplying future savings for a month by the no. of months in future in the selected period."
              : "These charts show the Snowflake savings data for the period specified for Current state. Saving may be coming from autonomous agent actions or your team implementing the recommendations shown as opportunities. Charts can aggregate data at daily, weekly or monthly level. Don't miss the DataPilot icon in the top right corner of the chart to start talking with the data!"}
          </p>
        }
      />
      <Stack>
        {error ? (
          <FailureComponent title={"Failed to load savings breakdown. Please try again later."} />
        ) : (
          <>
            <div className={classes.tileWrap}>
              <Tile
                color="#01CD8C"
                title="Total Money Savings"
                helpText={
                  isFuture
                    ? savingsPeriod === "thisYear"
                      ? `Projected money savings by the product from ${dayjs().format(
                          "DD MMM YYYY"
                        )} till 31 Dec ${dayjs().format("YYYY")}`
                      : `Projected money savings by the product from ${dayjs().format(
                          "DD MMM YYYY"
                        )} till ${dayjs().add(1, "year").subtract(1, "day").format("DD MMM YYYY")}`
                    : `Total money saved by the product during the period selected: ${formatDate(
                        currentStartDate,
                        yearMode
                      )} to ${formatDate(currentEndDate, yearMode)}`
                }
                value={
                  <NumberFormatter
                    value={
                      isFuture
                        ? (overallSavingsData as OverallSavingsEstimateResponse)
                            ?.total_savings_estimate
                        : overallSavingsData?.total_savings
                    }
                    options={{ currency }}
                  />
                }
              >
                <Stack direction="column">
                  <Tile
                    title="Autonomous Money Savings"
                    helpText={
                      isFuture
                        ? savingsPeriod === "thisYear"
                          ? `Projected money savings by the product from ${dayjs().format(
                              "DD MMM YYYY"
                            )} till 31 Dec ${dayjs().format("YYYY")}`
                          : `Projected money savings by the product from ${dayjs().format(
                              "DD MMM YYYY"
                            )} till ${dayjs().add(1, "year").subtract(1, "day").format("DD MMM YYYY")}`
                        : "Money saved automatically by the product during the period, without users doing anything"
                    }
                    value={getSavingsValueOrRange()}
                  />
                  <Tile
                    title="Assisted Money Savings"
                    helpText={
                      isFuture
                        ? savingsPeriod === "thisYear"
                          ? `Projected money saved from ${dayjs().format(
                              "DD MMM YYYY"
                            )} till 31 Dec ${dayjs().format(
                              "YYYY"
                            )} if users implement the opportunities identified`
                          : `Projected money saved from ${dayjs().format(
                              "DD MMM YYYY"
                            )} till ${dayjs()
                              .add(1, "year")
                              .subtract(1, "day")
                              .format(
                                "DD MMM YYYY"
                              )} if users implement the opportunities identified`
                        : "Money saved during the period when users implemented the opportunities identified by the product."
                    }
                    value={
                      <NumberFormatter
                        value={
                          isFuture
                            ? (overallSavingsData as OverallSavingsEstimateResponse)
                                ?.total_assisted_savings_estimate
                            : overallSavingsData?.total_assisted_savings
                        }
                        options={{ currency }}
                      />
                    }
                  />
                </Stack>
              </Tile>

              <Tile
                title="Total Time Savings"
                helpText={
                  isFuture
                    ? savingsPeriod === "thisYear"
                      ? `Projected time saved from ${dayjs().format(
                          "DD MMM YYYY"
                        )} till 31 Dec ${dayjs().format(
                          "YYYY"
                        )} if users implement the opportunities identified`
                      : `Projected time saved from ${dayjs().format("DD MMM YYYY")} till ${dayjs()
                          .add(1, "year")
                          .subtract(1, "day")
                          .format("DD MMM YYYY")} if users implement the opportunities identified`
                    : `Time saved during the period when users implemented the opportunities identified by the product.`
                }
                value={
                  isFuture
                    ? (overallSavingsData as OverallSavingsEstimateResponse)
                        ?.total_time_saved_estimate
                    : overallSavingsData?.time_saved
                }
                className={classes.time_saved}
              />
            </div>
            <Stack direction="column" className={classes.graphs}>
              {isFuture ? (
                <SavingsInFuture data={overallSavingsData as OverallSavingsEstimateResponse} />
              ) : (
                <>
                  <Stack className="al-justify-between">
                    {" "}
                    <Tabs
                      items={[
                        { label: "Autonomous", value: "autonomous" },
                        { label: "Assisted", value: "assisted" },
                      ]}
                      selectedTab={savingsType}
                      onTabSelect={setSavingsType}
                    />
                    <Stack className="al-items-center">
                      {savingsType !== "assisted" && (
                        <AggregateFilter
                          onSelect={onAggregateSelect}
                          aggregateBy={aggregateBy}
                          isFuture={isFuture}
                          startDate={currentStartDate}
                          endDate={currentEndDate}
                        />
                      )}
                    </Stack>
                  </Stack>
                  <div className="al-flex-1">
                    {savingsType === "autonomous" ? (
                      <div style={{ height: 400, padding: "1rem" }}>
                        <AutonomousSavingsSection
                          yearMode={yearMode}
                          currency={currency}
                          data={data as AutoTuneSavingsSummaryResponse | undefined}
                          isFetching={isFetching}
                          savingsType={savingsType}
                          aggregateBy={aggregateBy}
                          isFuture={isFuture}
                          totalCostEntities={totalCostEntities}
                        />
                      </div>
                    ) : null}
                    {savingsType === "assisted" ? (
                      <AssistedSavingsSection {...(data as AssistedSavingsResponse)} />
                    ) : null}
                  </div>
                </>
              )}
            </Stack>
          </>
        )}
      </Stack>
    </Card>
  );
};

export default SavingsBreakdown;
